import { computed, useRoute, useI18n, useRuntimeConfig } from '#imports';
import { AUTH_SIDEBAR_NAV_ITEMS } from 'r3ui-lib';
import { useAuthStore } from '~/stores/auth';
import { getUniqueKey } from '~/utils/helpers';
import { translate } from '~/i18n/i18n.config';
import { canViewAccessRequestsSection } from '~/composables/accessRequests/constants/accessRequestPermissions';

export type SidebarLink = {
  level: number;
  uniqueKey: string | null;
  isVisible: boolean;
  icon: string;
  label: string;
  isActive: boolean;
  href?: string;
  childrens?: SidebarLink[];
  isExternal?: boolean;
  openInNewWindow?: boolean;
};

function getChildrensIsActive(links: SidebarLink[]): boolean {
  const [firstLink] = links;
  if (!firstLink?.childrens?.length) return false;
  return (
    firstLink.childrens.find((item: SidebarLink) => Boolean(item.isActive))
      ?.isActive ?? false
  );
}

const AUTH_NAV_LABEL_KEYS: Record<
  (typeof AUTH_SIDEBAR_NAV_ITEMS)[number]['href'],
  string
> = {
  '/users': 'routes.users',
  '/organizations': 'routes.organizations',
  '/access-requests': 'routes.access_requests',
};

function isAuthNavItemVisible(
  hasPermission: (permission: string) => boolean,
  item: (typeof AUTH_SIDEBAR_NAV_ITEMS)[number]
): boolean {
  const permissions =
    'permissions' in item && Array.isArray(item.permissions)
      ? item.permissions
      : null;

  if (permissions?.length) {
    return permissions.some(hasPermission);
  }

  if (item.href === '/access-requests') {
    return canViewAccessRequestsSection(hasPermission);
  }

  if (!item.permission) {
    return false;
  }

  return hasPermission(item.permission);
}

export function useSidebarNav() {
  const { t } = useI18n();
  const authStore = useAuthStore();
  const route = useRoute();
  const config = useRuntimeConfig();

  const mainLinks = computed<SidebarLink[]>(() => {
    const hasPermission = authStore.hasUserPermission;

    const authChildren: SidebarLink[] = AUTH_SIDEBAR_NAV_ITEMS.map(item => {
      const labelKey = AUTH_NAV_LABEL_KEYS[item.href];
      if (!labelKey) throw new Error(`Неизвестный пункт меню: ${item.href}`);
      return {
        level: 1,
        uniqueKey: String(getUniqueKey()),
        isVisible: isAuthNavItemVisible(hasPermission, item),
        href: item.href,
        icon: ``,
        label: t(labelKey),
        isActive: route.path.includes(item.href),
      };
    });

    const links: SidebarLink[] = [
      {
        level: 0,
        uniqueKey: String(getUniqueKey()),
        isVisible: AUTH_SIDEBAR_NAV_ITEMS.some(item =>
          isAuthNavItemVisible(hasPermission, item)
        ),
        icon: `<i class="bi bi-person-square"></i>`,
        label: t('auth.Authorization'),
        isActive: false,
        childrens: authChildren,
      },
    ];

    const root = links[0];
    if (root) {
      root.isActive = getChildrensIsActive(links);
    }
    return links;
  });

  const additionalLinks = computed<SidebarLink[]>(() => [
    {
      level: 0,
      uniqueKey: String(getUniqueKey()),
      isVisible: true,
      href: config?.public?.registryLink as string,
      icon: `<i class="bi bi-file-earmark-text-fill"></i>`,
      label: t('routes.registry'),
      isActive: false,
      isExternal: true,
      openInNewWindow: false,
    },
    {
      level: 0,
      uniqueKey: String(getUniqueKey()),
      isVisible: authStore.hasUserPermission('sidebar-view-handbook'),
      href: config?.public?.handbookLink as string,
      icon: `<i class="bi bi-book-fill"></i>`,
      label: t('routes.handbook'),
      isActive: false,
      isExternal: true,
      openInNewWindow: false,
    },
  ]);

  const sidebarLinks = computed<SidebarLink[]>(() => [
    ...mainLinks.value,
    ...additionalLinks.value,
  ]);

  const dropdownLinks = computed(() => [
    {
      name: 'profile',
      title: translate('auth.profile'),
    },
    {
      name: 'logout',
      title: translate('auth.Logout'),
    },
  ]);

  return {
    sidebarLinks,
    dropdownLinks,
  };
}
